import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Cohort {
  id: string
  type: string
  name: string
  definition: string
  size: number
  retention_rate: number
  avg_lifetime_value: number
  churn_rate: number
}

interface Lifecycle {
  id: string
  stage: string
  customer_count: number
  percentage: number
  avg_days_in_stage: number
  engagement_score: number
  recommended_action: string
}

interface Statistics {
  total_cohorts: number
  active_cohorts: number
  avg_retention_rate: number
  avg_churn_rate: number
  total_customers_tracked: number
  avg_ltv: number
  retention_improvement: number
  analysis_frequency: string
}

export default function CohortAnalysisPage() {
  const [cohorts, setCohorts] = useState<Cohort[]>([])
  const [lifecycle, setLifecycle] = useState<Lifecycle[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [activeTab, setActiveTab] = useState<'cohorts' | 'lifecycle'>('cohorts')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [cohortsRes, lifecycleRes, statsRes] = await Promise.all([
        fetch('/api/cohortanalysis/cohorts'),
        fetch('/api/cohortanalysis/lifecycle'),
        fetch('/api/cohortanalysis/statistics')
      ])

      if (cohortsRes.ok) setCohorts(await cohortsRes.json())
      if (lifecycleRes.ok) setLifecycle(await lifecycleRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching cohort analysis data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStageColor = (stage: string) => {
    const colors: Record<string, string> = {
      new: 'bg-blue-100 text-blue-800',
      active: 'bg-green-100 text-green-800',
      at_risk: 'bg-yellow-100 text-yellow-800',
      dormant: 'bg-orange-100 text-orange-800',
      churned: 'bg-red-100 text-red-800',
      reactivated: 'bg-purple-100 text-purple-800'
    }
    return colors[stage] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Cohort Analysis</h1>
          <p className="mt-2 text-gray-600">Customer retention and lifecycle analytics</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active Cohorts</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.active_cohorts}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Retention</p>
              <p className="text-2xl font-bold text-green-600">{(statistics.avg_retention_rate * 100).toFixed(1)}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Churn Rate</p>
              <p className="text-2xl font-bold text-red-600">{(statistics.avg_churn_rate * 100).toFixed(1)}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg LTV</p>
              <p className="text-2xl font-bold text-blue-600">${statistics.avg_ltv.toLocaleString()}</p>
            </div>
          </div>
        )}

        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('cohorts')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'cohorts' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'
              }`}
            >
              Cohorts ({cohorts.length})
            </button>
            <button
              onClick={() => setActiveTab('lifecycle')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'lifecycle' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'
              }`}
            >
              Lifecycle ({lifecycle.length})
            </button>
          </nav>
        </div>

        {activeTab === 'cohorts' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cohort</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Retention</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg LTV</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Churn</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {cohorts.slice(0, 15).map((cohort) => (
                  <tr key={cohort.id}>
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{cohort.name}</p>
                      <p className="text-sm text-gray-500">{cohort.definition}</p>
                    </td>
                    <td className="px-6 py-4 text-gray-500 capitalize">{cohort.type}</td>
                    <td className="px-6 py-4 text-gray-500">{cohort.size.toLocaleString()}</td>
                    <td className="px-6 py-4 text-green-600 font-medium">{(cohort.retention_rate * 100).toFixed(1)}%</td>
                    <td className="px-6 py-4 text-gray-900">${cohort.avg_lifetime_value.toLocaleString()}</td>
                    <td className="px-6 py-4 text-red-600">{(cohort.churn_rate * 100).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'lifecycle' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lifecycle.map((stage) => (
              <div key={stage.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start">
                  <h3 className="text-lg font-semibold text-gray-900 capitalize">{stage.stage.replace('_', ' ')}</h3>
                  <span className={`px-2 py-1 text-xs rounded-full ${getStageColor(stage.stage)}`}>
                    {(stage.percentage * 100).toFixed(0)}%
                  </span>
                </div>
                <p className="text-3xl font-bold text-gray-900 mt-4">{stage.customer_count.toLocaleString()}</p>
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Avg Days in Stage</span>
                    <span className="text-gray-900">{stage.avg_days_in_stage}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Engagement</span>
                    <span className="text-gray-900">{(stage.engagement_score * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Action</span>
                    <span className="text-blue-600 capitalize">{stage.recommended_action.replace('_', ' ')}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
